import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TextField, Checkbox, FormControlLabel, Button, Grid, Typography, Paper, MenuItem,IconButton,FormGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAuth from './useAuth.js';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNotification } from "../../components/NotificationContext";
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.jpg,.jpeg,.png,.webp,.heic,.heif,.avif";
const isAllowedImage = (file) => {
  const name = file.name.toLowerCase();
  return file.size <= MAX_IMAGE_SIZE && (
    file.type.startsWith("image/") ||
    [".heic", ".heif", ".avif"].some((ext) => name.endsWith(ext))
  );
};
const ProductEdit = () => {
  useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productData, setProductData] = useState({
    name: "",
    category: "",
    description: "",
    photo_url: "",
    price: "",
    sort_order: 0,
    in_stock: false,
    showColors: false,
    colors: [],
    colorStock: {},
    showSizes: false,
    sizes: [],
    sizeStock: {},
    product_images: [],
  });
  const [multipleImages, setMultipleImages] = useState([]);
  const [multipleImagePreviews, setMultipleImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const showNotification = useNotification();
  const navigate = useNavigate();


  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
  
        const [productRes, categoryRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/product`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/categories`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
  
        const products = productRes.data;

        const transformedProducts = products.map((product) => {
          const colors = product.colors?.map(c => c.color_name) || [];
          const colorStock = {};
          product.colors?.forEach(c => {
            colorStock[c.color_name] = c.in_stock;
          });
  
          const sizes = product.sizes?.map(s => s.size_name) || [];
          const sizeStock = {};
          product.sizes?.forEach(s => {
            sizeStock[s.size_name] = s.in_stock;
          });

          return {
            ...product,
            colors,
            colorStock,
            sizes,
            sizeStock,
            showColors: colors.length > 0,
            showSizes: sizes.length > 0
          };
        });
  
        setProducts(transformedProducts);
        setCategories(categoryRes.data);
      } catch (error) {
        showNotification("Veriler getirilemedi!", "error");
      }
    };
  
    fetchData();
  }, []);
  
  useEffect(() => {
    if (selectedSubCategory) {
      const filtered = products.filter(
        (p) => Number(p.category) === selectedSubCategory
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [selectedSubCategory, products]);
  useEffect(() => {
    if (selectedProduct?.product_images) {
      setExistingImages(selectedProduct.product_images);
    }
  }, [selectedProduct]);
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductData({
      description: product.description,
      name: product.name,
      price: product.price,
      in_stock: product.in_stock,
      sort_order: product.sort_order,
      colors: product.colors || [],
      colorStock: product.colorStock || {},
      showColors: product.showColors || false,
      sizes: product.sizes || [],
      sizeStock: product.sizeStock || {},
      showSizes: product.showSizes || false,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = () => {
    setProductData((prevData) => ({
      ...prevData,
      in_stock: !prevData.in_stock,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!isAllowedImage(file)) {
        showNotification('Gorsel 10 MB altinda ve desteklenen formatta olmali!', 'error');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(file);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files).filter((file) => {
      const allowed = isAllowedImage(file);
      if (!allowed) {
        showNotification('Desteklenmeyen veya 10 MB ustu gorsel atlandi!', 'error');
      }
      return allowed;
    });
    const newPreviews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setMultipleImages((prev) => [...prev, ...files]);
    setMultipleImagePreviews((prev) => [...prev, ...newPreviews]);
  };
  const handleRemoveMultipleImage = (index) => {
    const updatedFiles = [...multipleImages];
    const updatedPreviews = [...multipleImagePreviews];
  
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
  
    setMultipleImages(updatedFiles);
    setMultipleImagePreviews(updatedPreviews);
  };
  const handleRemoveExistingImage = (index) => {
    const updated = [...existingImages];
    const removed = updated.splice(index, 1); // çıkarılan görseli al
    setExistingImages(updated);
    setRemovedImages((prev) => [...prev, removed[0]]); // silinenleri listele
  };
  const handleShowColorsChange = (event) => {
    const { checked } = event.target;
    setProductData((prevData) => ({
      ...prevData,
      showColors: checked,  // Renklerin görünürlüğünü güncelle
    }));
  };
  
  const handleShowSizesChange = (event) => {
    const { checked } = event.target;
    setProductData((prevData) => ({
      ...prevData,
      showSizes: checked,  // Bedenlerin görünürlüğünü güncelle
    }));
  };
  const handleColorChange = (e) => {
    const { name, checked } = e.target;
    setProductData((prev) => {
      const updatedColors = checked
        ? [...(prev.colors || []), name]
        : (prev.colors || []).filter((c) => c !== name);
      return {
        ...prev,
        colors: updatedColors,
      };
    });
  };
  
  const handleSizeChange = (event) => {
    const { name, checked } = event.target;
    setProductData((prevData) => ({
      ...prevData,
      sizes: checked 
        ? [...prevData.sizes, name]  // Yeni beden ekleyin
        : prevData.sizes.filter((size) => size !== name), // Beden çıkarın
    }));
  };
  const handleColorStockChange = (e) => {
    const { name, checked } = e.target;
    setProductData((prev) => ({
      ...prev,
      colorStock: {
        ...(prev.colorStock || {}),
        [name]: checked,
      },
    }));
  };
  
  const handleSizeStockChange = (event) => {
    const { name, checked } = event.target;
    setProductData(prevState => ({
      ...prevState,
      sizeStock: {
        ...prevState.sizeStock,
        [name]: checked,
      }
    }));
  };
  
  const handleDelete = async () => {
    setLoading(true);
    if (!selectedProduct) return;
  
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/product/${selectedProduct.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      showNotification("Ürün Silme Başarılı!", "success");
      localStorage.setItem("notificationMessage", "Ürün Silme Başarılı!");
      localStorage.setItem("notificationSeverity", "success");
      setLoading(false);
      setProducts(products.filter((product) => product.id !== selectedProduct.id));
      setSelectedProduct(null);
      setProductData({
        description: '',
        name: '',
        price: '',
        in_stock: false,
      });
      setImagePreview('');
    } catch (error) {
      showNotification("Ürün Silme Yapılamadı!", "error");
      setLoading(false);
    }
  };
  
  const handleBackClick = () => {
    navigate(-1);
  };


  const handleSubmit = async (e) => {
    setLoading(true);
    const cleanColors = (productData.colors || []).filter(color => 
      ['Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Beyaz', 'Siyah'].includes(color)
    );
    
    const cleanSizes = (productData.sizes || []).filter(size => 
      ['XS', 'S', 'M', 'L', 'XL'].includes(size)
    );
    
    // ColorStock ve sizeStock'u temizle
    const cleanColorStock = Object.fromEntries(
      Object.entries(productData.colorStock || {})
        .filter(([key]) => cleanColors.includes(key))
    );
    
    const cleanSizeStock = Object.fromEntries(
      Object.entries(productData.sizeStock || {})
        .filter(([key]) => cleanSizes.includes(key))
    );
    e.preventDefault();
    if (!selectedProduct) return;
  
    const token = localStorage.getItem('token');
  
    const formData = new FormData();
    multipleImagePreviews.forEach((img) => {
      formData.append('new_images', img.file);
    });
    formData.append('removed_images', JSON.stringify(removedImages));  
    formData.append('name', productData.name);
    formData.append('description', productData.description);
    formData.append('price', productData.price);
    formData.append('in_stock', productData.in_stock);
    formData.append('sort_order', productData.sort_order);
    // Temizlenmiş verileri ekle
    formData.append('colors', JSON.stringify(cleanColors));
    formData.append('colorStock', JSON.stringify(cleanColorStock));
    formData.append('showColors', productData.showColors);
    
    formData.append('sizes', JSON.stringify(cleanSizes));
    formData.append('sizeStock', JSON.stringify(cleanSizeStock));
    formData.append('showSizes', productData.showSizes);
    
    if (image) {
      formData.append('photo_url', image);
    } else if (selectedProduct.photo_url) {
      formData.append('photo_url', selectedProduct.photo_url);
    }
  
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/product/${selectedProduct.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showNotification("Ürün Güncelleme Başarılı!", "success");
      localStorage.setItem("notificationMessage", "Ürün Güncelleme Başarılı!");
      localStorage.setItem("notificationSeverity", "success");
      setLoading(false);
      setTimeout(() => {
        navigate('/admin');
      });
    } catch (error) {
      showNotification("Ürün Güncelleme Yapılamadı!", "error");
      setLoading(false);
    }
  };
  

  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, md: 4 } }}>
      <Grid size={{ xs: 12, sm: 10,md:10 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1, backgroundColor: '#ffffff' }}>
        <IconButton onClick={handleBackClick} style={{ marginRight: '10px' }}>
        <ArrowBackIcon />
      </IconButton>
          <Typography variant="h4" gutterBottom align="center" style={{ fontWeight: 600, color: '#3f51b5' }}>
            Ürün Güncelle
          </Typography>
          <TextField
  select
  label="Ana Kategori Seç"
  value={selectedCategory?.id || ''}
  onChange={(e) => {
    const selected = categories.find(cat => cat.id === Number(e.target.value));
    setSelectedCategory(selected);
    setSelectedSubCategory(null);
    setSelectedProduct(null);
  }}
  fullWidth
>
  {categories.map((cat) => (
    <MenuItem key={cat.id} value={cat.id}>
      {cat.name}
    </MenuItem>
  ))}
</TextField>

{/* Alt Kategori Seçimi */}
{selectedCategory && (
  <TextField
    select
    label="Alt Kategori Seç"
    value={selectedSubCategory || ''}
    onChange={(e) => {
      setSelectedSubCategory(Number(e.target.value));
      setSelectedProduct(null);
    }}
    fullWidth
    sx={{ mt: 2 }}
  >
    {selectedCategory.subCategories.map((sub) => (
      <MenuItem key={sub.id} value={sub.id}>
        {sub.name}
      </MenuItem>
    ))}
  </TextField>
)}

{/* Ürün Seçimi */}
{selectedSubCategory && (
  <TextField
    select
    label="Ürün Seç"
    value={selectedProduct?.id || ''}
    onChange={(e) => {
      const prod = filteredProducts.find((p) => p.id === Number(e.target.value));
      handleProductSelect(prod);
    }}
    fullWidth
    sx={{ mt: 2 }}
  >
    {filteredProducts.map((product) => (
      <MenuItem key={product.id} value={product.id}>
          <img 
            src={`${process.env.REACT_APP_BASE_URL}${product.photo_url}`}
            alt={product.name}
            style={{ width: '40px', height: '40px', marginRight: '10px', borderRadius: '4px' }}
          />
          {product.name}
        </MenuItem>
    ))}
  </TextField>
)}


          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Ürün ismi"
              name="name"
              value={productData.name}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              style={{ marginBottom: '15px' }}
            />
           <TextField
      name="description"
      label="Açıklama"
      value={productData.description}
      onChange={handleChange}
      placeholder="Açıklama"
      multiline
      minRows={4}
      maxRows={6}
      variant="outlined"
      fullWidth
      style={{
        marginBottom: '15px',
        fontSize: '16px',
      }}
    />
            <TextField
              fullWidth
              label="Ücret"
              name="price"
              value={productData.price}
              onChange={handleChange}
              margin="normal"
              variant="outlined"

              style={{ marginBottom: '15px' }}
            />
                        <TextField
              fullWidth
              label="Ürün Sırası"
              name="sort_order"
              value={productData.sort_order ?? ""}
              onChange={handleChange}
              margin="normal"
              variant="outlined"

              style={{ marginBottom: '15px' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={productData.in_stock}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="Stockta var mı"
              style={{ marginBottom: '10px' }}
            />
            {/* Renk Stoğu */}
{productData.in_stock && (
  <FormControlLabel
    control={
      <Checkbox
        checked={!!productData.showColors} // Eğer renkler gösterilsin isteniyorsa true olmalı
        onChange={handleShowColorsChange} // Renklerin görünürlüğünü değiştiren fonksiyon
        color="primary"
      />
    }
    label="Renk Stoğu"
    style={{ marginBottom: '10px' }}
  />
)}


{productData.showColors && productData.in_stock && (
 <>
 <Typography variant="h6" style={{ marginBottom: '10px' }}>Renk</Typography>
 <FormGroup style={{ marginBottom: '20px' }}>
   {['Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Beyaz', 'Siyah', 'Turuncu', 'Pembe', 'Mor', 'Gri', 'Kahverengi'].map((color) => (
     <div key={color}>
       {/* Renk seçimi */}
       <FormControlLabel
         control={
           <Checkbox
             checked={Array.isArray(productData.colors) && productData.colors.includes(color)}
             onChange={handleColorChange}
             name={color}
             color="primary"
           />
         }
         label={color}
       />

       {/* Eğer renk seçiliyse, stok durumu göster */}
       {Array.isArray(productData.colors) && productData.colors.includes(color) && (
         <FormControlLabel
           control={
             <Checkbox
               checked={productData.colorStock?.[color] ?? false}
               onChange={handleColorStockChange}
               name={color}
               color="primary"
             />
           }
           label={`Stokta var mı (${color})`}
           style={{ marginLeft: "30px" }}
         />
       )}
     </div>
   ))}
 </FormGroup>
</>

)}

{/* Beden seçenekleri */}
{productData.in_stock && (
  <FormControlLabel
    control={
      <Checkbox
        checked={!!productData.showSizes} // Eğer bedenler gösterilsin isteniyorsa true olmalı
        onChange={handleShowSizesChange} // Bedenlerin görünürlüğünü değiştiren fonksiyon
        color="primary"
      />
    }
    label="Beden Stoğu"
    style={{ marginBottom: '10px' }}
  />
)}

{productData.showSizes && productData.in_stock && (
 <>
 <Typography variant="h6" style={{ marginBottom: '10px' }}>Beden</Typography>
 <FormGroup style={{ marginBottom: '20px' }}>
   {['XS', 'S', 'M', 'L', 'XL'].map((size) => (
     <div key={size}>
       {/* Beden seçimi */}
       <FormControlLabel
         control={
           <Checkbox
             checked={productData.sizes?.includes(size) ?? false}
             onChange={handleSizeChange}
             name={size}
             color="primary"
           />
         }
         label={size}
       />

       {/* Sadece seçili bedenler için stok durumu göster */}
       {productData.sizes?.includes(size) && (
         <FormControlLabel
           control={
             <Checkbox
               checked={productData.sizeStock?.[size] || false}
               onChange={handleSizeStockChange}
               name={size}
               color="primary"
             />
           }
           label={`Stokta var mı (${size})`}
           style={{ marginLeft: "30px" }}
         />
       )}
     </div>
   ))}
 </FormGroup>
</>

)}
            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="upload-image"
              />
              <label htmlFor="upload-image">
                <Button variant="contained" color="secondary" component="span">
                  Fotoğraf Yükle
                </Button>
              </label>
              <div
  style={{
    marginTop: '10px',
    backgroundColor: 'grey',
    padding: '10px',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  }}
>
  <Typography
    variant="h5"
    style={{ fontWeight: 'bold', color: '#fff', textAlign: 'center' }}
  >
    Fotoğraflar
  </Typography>
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginTop: '10px' }}>
    {/* Önceden Yüklenmiş Fotoğraf */}
    <div style={{ textAlign: 'center' }}>
      <Typography
        variant="subtitle1"
        style={{ color: '#fff', fontWeight: 'bold' }}
      >
        Önceki Fotoğraf
      </Typography>
      {selectedProduct?.photo_url ? (
        <img
          src={`${process.env.REACT_APP_BASE_URL}${selectedProduct.photo_url}`}
          alt="Önceki Fotoğraf"
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            objectFit: 'contain',
            borderRadius: '8px',
            border: '2px solid gold',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        />
      ) : (
        <Typography
          variant="body2"
          style={{
            color: '#fff',
            textAlign: 'center',
            fontStyle: 'italic',
            height: '300px',
            width: '200px',
            border: '2px solid gold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Önceki fotoğraf mevcut değil.
        </Typography>
      )}
    </div>

    {/* Yeni Seçilen Fotoğraf */}
    <div style={{ textAlign: 'center' }}>
      <Typography
        variant="subtitle1"
        style={{ color: '#fff', fontWeight: 'bold' }}
      >
        Yeni Fotoğraf
      </Typography>
      {imagePreview ? (
        <div style={{ position: 'relative' }}>
          <img
            src={imagePreview}
            alt="Yeni Fotoğraf"
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              objectFit: 'contain',
              borderRadius: '8px',
              border: '2px solid gold',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          />
          <button
            onClick={() => {
              setImage(null); // Yeni fotoğrafı sıfırla
              setImagePreview(''); // Görsel önizlemeyi sıfırla
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: 'red',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              padding: '5px 10px',
              fontSize: '16px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            ✕
          </button>
        </div>
      ) : (
        <Typography
          variant="body2"
          style={{
            color: '#fff',
            textAlign: 'center',
            fontStyle: 'italic',
            height: '300px',
            width: '200px',
            border: '2px solid gold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Yeni fotoğraf seçilmedi.
        </Typography>
      )}
    </div>
  </div>
</div>
<div style={{ marginBottom: '20px',marginTop: '20px' }}>
  <input
    type="file"
    accept={IMAGE_ACCEPT}
    multiple
    onChange={handleMultipleFileChange}
    style={{ display: 'none' }}
    id="upload-multiple-images"
  />
  <label htmlFor="upload-multiple-images">
    <Button variant="contained" color="secondary" component="span" style={{ marginRight: "10px" }}>
      Çoklu Fotoğraf Yükle
    </Button>
  </label>
  <Typography variant="h6" style={{ marginTop: '10px', fontWeight: 500 }}>Seçilen Fotoğraflar</Typography>
  <Grid container spacing={2} style={{ marginTop: '10px' }}>
  {/* Daha önce yüklenen fotoğraflar */}
  {existingImages.map((url, index) => (
    <Grid key={`existing-${index}`}>
      <div style={{ position: 'relative' }}>
        <img
          src={`${process.env.REACT_APP_BASE_URL}${url}`}
          alt={`Existing ${index}`}
          style={{
            width: '100px',
            height: '100px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '2px solid green',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        />
        <Button
          size="small"
          onClick={() => handleRemoveExistingImage(index)}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            minWidth: '30px',
            padding: '0px',
            backgroundColor: 'rgba(255,0,0,0.8)',
            color: '#fff',
            borderRadius: '50%',
            fontWeight: 'bold'
          }}
        >
          X
        </Button>
      </div>
    </Grid>
  ))}

  {/* Yeni seçilen fotoğraflar */}
  {multipleImagePreviews.map((img, index) => (
    <Grid key={`new-${index}`}>
      <div style={{ position: 'relative' }}>
        <img
          src={img.preview}
          alt={`Preview ${index}`}
          style={{
            width: '100px',
            height: '100px',
            objectFit: 'cover',
            borderRadius: '8px',
            border: '2px solid gold',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
        />
        <Button
          size="small"
          onClick={() => handleRemoveMultipleImage(index)}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            minWidth: '30px',
            padding: '0px',
            backgroundColor: 'rgba(255,0,0,0.8)',
            color: '#fff',
            borderRadius: '50%',
            fontWeight: 'bold'
          }}
        >
          X
        </Button>
      </div>
    </Grid>
  ))}
</Grid>

</div>

            </div>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              style={{
                marginTop: '20px',
                padding: '12px',
                fontWeight: 600,
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = '#303f9f')}
              onMouseOut={(e) => (e.target.style.backgroundColor = '#3f51b5')}
            >
              {loading ? "Güncelleniyor..." : "Güncelle"}
            </Button>
          </form>

          <Button
            fullWidth
            variant="contained"
            color="secondary"
            disabled={loading}
            style={{
                marginTop: '10px',
                padding: '12px',
                fontWeight: 600,
                backgroundColor: '#f50057',
                transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#c51162')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#f50057')}
            onClick={handleDelete}
            >
            {loading ? "Siliniyor..." : "Sil"}
            </Button>



        </Paper>
      </Grid>


    </Grid>
  );
};

export default ProductEdit;
